import { Cart_ApplicationFragment } from "rental/generated.graphql";
import { useSessionBooleanState } from "rental/hooks/sessionState";
import { useElementId } from "shared/utils/elementIds";
import cs from 'classnames';


export const useUnderstoodMinPeriodsState = (application: Cart_ApplicationFragment) => {
  /**
   * Used for storing whether a customer has clicked the "I agree" checkbox to say they understand the min rental periods of each item.
   * 
   * @remarks
   * This stores the state in window.sessionStorage so that it persists when a customer refreshes or browses back to the cart page.
   * This does not need to be stored serverside.
   * A sessionStorage key is generated based on the applicationid and itemId's so that the customer
   * needs to re-tick the checkbox if the cart items change.
   */

  const itemIds = application.items?.map(item => item.id).sort((a, b) => a - b).join(',') ?? '';
  const sessionKey = `understoodMinPeriods-${application.id}:${itemIds}`;
  const [state, setState] = useSessionBooleanState(sessionKey, false);
  return {
    checked: state,
    setChecked: setState,
  }
};

export type UnderstoodMinPeriods = ReturnType<typeof useUnderstoodMinPeriodsState> & {
  validated: boolean
  invalid: boolean
  itemsValid: boolean
};


export const UnderstoodMinPeriodsDesc = (props: {invalid: boolean}) => {
  return <>
  <div>I have reviewed the items in my cart and understand the minimum rental period I have agreed to for each item:</div>
  {props.invalid && <div className="text-danger">
    You must agree before submitting.
  </div>}</>;
}


export const UnderstoodMinPeriodsCheckbox = (props: UnderstoodMinPeriods) => {
  const understandsMinRentalId = useElementId('understandsMinRental');
  const errorId = understandsMinRentalId + '-error'
  return (
    <div className="form-check">
      <input
        className={cs('form-check-input', + props.invalid && 'is-invalid')}
        type="checkbox"
        checked={props.checked}
        id={understandsMinRentalId}
        onChange={e => props.setChecked(e.target.checked)}
        aria-describedby={props.invalid ? errorId : understandsMinRentalId}
      />
      <label className="form-check-label" htmlFor={understandsMinRentalId}>
        I agree
      </label>
    </div>
  );
};
